import { WidgetSlot, WidgetSlotConfiguration } from '@/api/rest/resources/types/widget';
import { useGetIndicatorProductSubscriptionConfigurations } from '@/hooks/useDashboardIndicatorProductSubscriptionConfiguration';
import { APPROVED_DEFAULT_FILTERS } from '@/hooks/useInSituIndicatorConfigurationWidgets';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';
import { printYear } from '@/utils/formatting/date';

type OrgCardProps = {
  indicatorProductSubscriptionConfigId: string | undefined;
  widgetConfiguration: WidgetSlotConfiguration & { widgets?: WidgetSlot[] };
};

export const useOrgCardWidget = ({
  widgetConfiguration,
  indicatorProductSubscriptionConfigId,
}: OrgCardProps): WidgetSlotConfiguration | null => {
  const isStaffUser = useIsStaffUser();
  const ipSubConfigurations = useGetIndicatorProductSubscriptionConfigurations(
    undefined,
    true,
    isStaffUser ? undefined : APPROVED_DEFAULT_FILTERS,
  );
  const sourceRef = indicatorProductSubscriptionConfigId
    ? ipSubConfigurations.data?.results.find((config) => config.id === indicatorProductSubscriptionConfigId)
        ?.file_info?.[0]?.id
    : undefined;

  return recurisvelyFormatWidgetConfiguration({
    widgetConfiguration,
    sourceRef,
  }) as WidgetSlotConfiguration;
};

const TICK_FORMATTERS: Record<string, (value: string) => string> = {
  printYear,
};

const recurisvelyFormatWidgetConfiguration = ({
  widgetConfiguration,
  sourceRef,
}: {
  widgetConfiguration: WidgetSlotConfiguration | WidgetSlot;
  sourceRef: string | undefined;
}): WidgetSlotConfiguration | WidgetSlot => {
  if (!widgetConfiguration.widgets) {
    return widgetConfiguration;
  }

  return {
    ...widgetConfiguration,
    widgets: widgetConfiguration.widgets.map((widget) => {
      let updatedWidget = widget;

      const tickFormatterRef = updatedWidget.relatedData?.graphTileProps?.contentProps?.xAxisProps?.tickFormatter;
      if (typeof tickFormatterRef === 'string' && TICK_FORMATTERS[`${tickFormatterRef}`]) {
        updatedWidget = {
          ...updatedWidget,
          relatedData: {
            ...updatedWidget.relatedData,
            graphTileProps: {
              ...updatedWidget.relatedData.graphTileProps,
              contentProps: {
                ...updatedWidget.relatedData.graphTileProps.contentProps,
                xAxisProps: {
                  ...updatedWidget.relatedData.graphTileProps.contentProps.xAxisProps,
                  tickFormatter: TICK_FORMATTERS[`${tickFormatterRef}`],
                },
              },
            },
          },
        };
      }

      if (sourceRef && updatedWidget?.relatedData?.graphTileProps?.contentProps) {
        updatedWidget = {
          ...updatedWidget,
          relatedData: {
            ...updatedWidget.relatedData,
            graphTileProps: {
              ...updatedWidget.relatedData.graphTileProps,
              contentProps: {
                ...updatedWidget.relatedData.graphTileProps.contentProps,
                sourceRef,
              },
            },
          },
        };
      }

      if (updatedWidget.widgets) {
        updatedWidget = recurisvelyFormatWidgetConfiguration({
          widgetConfiguration: updatedWidget,
          sourceRef,
        }) as WidgetSlot;
      }

      return updatedWidget;
    }),
  };
};
